import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { MessageSquare, ChevronDown, ChevronUp, Search, Filter } from 'lucide-react';

interface Conversation {
  id: string;
  session_id: string;
  visitor_name: string;
  visitor_email: string;
  source: string;
  messages: Array<{ role: string; text: string }>;
  ip_address: string | null;
  city: string | null;
  country: string | null;
  started_at: string;
  ended_at: string | null;
  created_at: string;
}

const ConversationsPage = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'alex' | 'playground'>('all');

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('chat_conversations')
      .select('*')
      .order('started_at', { ascending: false })
      .limit(200);

    if (!error && data) {
      setConversations(data as Conversation[]);
    }
    setLoading(false);
  };

  const filtered = conversations.filter(c => {
    if (sourceFilter !== 'all' && c.source !== sourceFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        c.visitor_name.toLowerCase().includes(q) ||
        c.visitor_email.toLowerCase().includes(q) ||
        (c.city || '').toLowerCase().includes(q) ||
        (c.country || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const toggle = (id: string) => setExpandedId(prev => prev === id ? null : id);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-pulse" style={{ color: 'var(--slate)' }}>Loading conversations…</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-fraunces font-bold" style={{ color: 'var(--blue)' }}>
            <MessageSquare className="inline w-6 h-6 mr-2" style={{ color: 'var(--amber)' }} />
            Conversations
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--slate)' }}>
            {conversations.length} total session{conversations.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--slate)' }} />
          <input
            type="text"
            placeholder="Search by name, email, or location..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--line)', background: 'var(--white)', color: 'var(--blue)' }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4" style={{ color: 'var(--slate)' }} />
          {(['all', 'alex', 'playground'] as const).map(s => (
            <button
              key={s}
              onClick={() => setSourceFilter(s)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              style={{
                background: sourceFilter === s ? 'var(--blue)' : 'var(--off-white)',
                color: sourceFilter === s ? 'var(--white)' : 'var(--slate)',
                border: `1px solid ${sourceFilter === s ? 'var(--blue)' : 'var(--line)'}`,
              }}
            >
              {s === 'all' ? 'All' : s === 'alex' ? 'Alex Chat' : 'Playground'}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="text-center py-12" style={{ color: 'var(--slate)' }}>
            No conversations found.
          </div>
        )}
        {filtered.map(c => (
          <div
            key={c.id}
            className="rounded-xl border overflow-hidden"
            style={{ borderColor: 'var(--line)', background: 'var(--white)' }}
          >
            {/* Header row */}
            <button
              onClick={() => toggle(c.id)}
              className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-[var(--off-white)] transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm" style={{ color: 'var(--blue)' }}>{c.visitor_name}</span>
                  <span className="text-xs" style={{ color: 'var(--slate)' }}>({c.visitor_email})</span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs" style={{ color: 'var(--slate)' }}>
                  <span>{format(new Date(c.started_at), 'dd MMM yyyy · HH:mm')}</span>
                  {c.city && <span>{c.city}, {c.country}</span>}
                  <span
                    className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                    style={{
                      background: c.source === 'alex' ? 'var(--amber)' : 'var(--blue-light)',
                      color: c.source === 'alex' ? 'var(--white)' : 'var(--blue)',
                    }}
                  >
                    {c.source === 'alex' ? 'Alex' : 'Playground'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: 'var(--slate)' }}>
                  {(c.messages as any[])?.length || 0} msgs
                </span>
                {expandedId === c.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </button>

            {/* Expanded messages */}
            {expandedId === c.id && (
              <div className="px-4 pb-4 pt-2" style={{ borderTop: '1px solid var(--line)' }}>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {(c.messages as any[] || []).map((msg: any, i: number) => (
                    <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className="px-3 py-2 rounded-lg text-sm max-w-[80%]"
                        style={{
                          background: msg.role === 'user' ? 'var(--blue)' : 'var(--off-white)',
                          color: msg.role === 'user' ? 'var(--white)' : 'var(--blue)',
                        }}
                      >
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  {(!c.messages || (c.messages as any[]).length === 0) && (
                    <p className="text-xs italic" style={{ color: 'var(--slate)' }}>No messages recorded.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConversationsPage;
